import { Cell, Flow, Grid, Heading, InfoLabel, Text } from 'bold-ui'
import { isProcedimentoAdministrativo } from 'components/form/field/select/ProcedimentoSelectField/ProcedimentoSelectFieldItem'
import { LoadingIndicator } from 'components/loading'
import {
  useHistoricoAtendimentoIndividualDwQuery,
  useHistoricoCiapFiltroQuery,
  useHistoricoConcatenadaProcedimentoQuery,
  useHistoricoConcatenadaZikaQuery,
  useHistoricoProcedimentoByCodigosDwQuery,
} from 'graphql/hooks.generated'
import {
  Ciap,
  FatoAtendDomProblemaCondicao,
  FatoAtendimentoIndividual,
  FatoAtendimentoIndividualProblemas,
  FatoAtendOdontoProblemas,
  Procedimento,
  TipoProcedimentoEnum,
} from 'graphql/types.generated'
import { memo } from 'react'
import { partition } from 'util/array'
import { calculateAge } from 'util/date/calculateAge'
import { stringGrouper } from 'util/string/string-grouper'
import { isEmpty } from 'util/validation/Util'
import {
  FichasConcatenadasHistorico,
  HistoricoPanelProps,
  historicoSubsectionStyles,
} from 'view/atendimentos/detail/historico/model/model-historico'
import {
  convertFatoMedicoesToHistoricoMedicaoModel,
  getMchatRiscoEnumByDimensaoMchatRiscoId,
  hasAllAttrsNull,
  hasMedicoes,
  mergeResultadosExamesDw,
  mountInfosAdministrativasAtendimentoCompartilhado,
  naoRegistradoNesseAtendimento,
} from 'view/atendimentos/detail/historico/utils/utils-historico'
import { MChatMarca } from 'view/atendimentos/detail/soap/objetivo/puericultura/mchat-rf/components/MChatMarca'

import HistoricoSection from '../../../../HistoricoSection'
import HistoricoAvaliacaoProblemasCondicoesDwTableContent from '../../../../table/dw/HistoricoAvaliacaoProblemasDwTableContent'
import HistoricoDetailTable from '../../../../table/HistoricoDetailTable'
import HistoricoGapPanel from '../../HistoricoGapPanel'
import { HistoricoIvcfTableBox } from '../../HistoricoIvcfTableBox'
import HistoricoNoDetailAvailable from '../../HistoricoNoDetailAvailable'
import HistoricoResultadoExamesTable from '../../HistoricoResultadoExamesTable'
import HistoricoMedicoesPanel from '../../medicoes/HistoricoMedicoesPanel'
import { ResultadoExamesModel } from '../../zika/historicoZikaModel'
import { ResultadosDeExamesZikaContent } from '../../zika/ResultadosDeExamesZikaContent'
import { HistoricoMChatRfTableBox } from '../puericultura/m-chat/HistoricoMChatRfTableBox'
import { HistoricoMChatTableBox } from '../puericultura/m-chat/HistoricoMChatTableBox'
import { HistoricoDwPlanoSection } from './components/HistoricoDwPlanoSection'
import { HistoricoDwSubjetivoSection } from './components/HistoricoSubjetivoDwSection'
import GravidezAvaliacaoSection from './GravidezAvaliacaoSection'
import { extractFromFiltro, isFiltroPreenchido } from './utils-HistoricoDwCiapFiltro'

interface HistoricoAtendimentoIndividualDwPanelProps extends HistoricoPanelProps {
  fichasConcatenadas: FichasConcatenadasHistorico
}

function HistoricoAtendimentoIndividualDwPanel(props: Readonly<HistoricoAtendimentoIndividualDwPanelProps>) {
  const { uuidRegistro, idadeOrigemAtendimento, cpfOuCnsCidadao, fichasConcatenadas } = props

  const {
    data: { historicoAtendimentoIndividualDw: historico } = {},
    loading,
  } = useHistoricoAtendimentoIndividualDwQuery({
    variables: { input: { isOrigemPec: false, uuidRegistro, cpfOuCnsCidadao } },
  })

  const {
    data: { historicoZikaDetail: zikaConcatenada } = {},
    loading: loadingZikaConcatenada,
  } = useHistoricoConcatenadaZikaQuery({
    variables: { input: { isOrigemPec: false, uuidRegistro: fichasConcatenadas?.uuidZika, cpfOuCnsCidadao } },
    skip: !fichasConcatenadas?.uuidZika,
  })

  const uuidFichaProcedimento = fichasConcatenadas?.uuidProcedimento ?? uuidRegistro
  const {
    data: { historicoProcedimentoDw: procedimentoConcatenada } = {},
    loading: loadingProcedimentoConcatenada,
  } = useHistoricoConcatenadaProcedimentoQuery({
    variables: { input: { isOrigemPec: false, uuidRegistro: uuidFichaProcedimento, cpfOuCnsCidadao } },
  })

  const {
    data: { procedimentosByCodigos: procedimentosDw } = {},
    loading: loadingProcedimentoDw,
  } = useHistoricoProcedimentoByCodigosDwQuery({
    variables: {
      codigos: procedimentoConcatenada?.procedimentos?.map((procedimento) => procedimento?.codigoProcedimento) || [],
    },
  })

  const { data: { ciapsByCodigos: ciap2Filtro } = {}, loading: loadingCiap2Filtro } = useHistoricoCiapFiltroQuery({
    variables: {
      input: {
        codigos: [
          ...extractFromFiltro(historico?.filtroCiapMotivoConsulta),
          ...extractFromFiltro(historico?.filtroCiapPlano),
        ],
      },
    },
    skip:
      !historico ||
      (!isFiltroPreenchido(historico.filtroCiapMotivoConsulta) && !isFiltroPreenchido(historico.filtroCiapPlano)),
  })

  if (
    loading ||
    loadingZikaConcatenada ||
    loadingProcedimentoConcatenada ||
    loadingCiap2Filtro ||
    loadingProcedimentoDw
  )
    return <LoadingIndicator />

  if (!historico) return <HistoricoNoDetailAvailable />

  const [procedimentosAdmin, procedimentosNaoAdmin] = partition(procedimentosDw, (procedimento: Procedimento) =>
    isProcedimentoAdministrativo(procedimento.tipoProcedimento as TipoProcedimentoEnum)
  )

  const medicoes = convertFatoMedicoesToHistoricoMedicaoModel(historico as FatoAtendimentoIndividual)

  const hasAnyMedicao = hasMedicoes(medicoes)
  const hasIdadeGestacional = !!historico.nuIdadeGestacional
  const hasMedicoesPreNatal = historico.stGravidezPlanejada || hasIdadeGestacional
  const hasExamesAvaliados = !isEmpty(historico.examesAvaliados) || !isEmpty(historico.resultadosExamesEspecificos)

  const hasProblemas = !isEmpty(historico.problemas)
  const hasNasfPolo = !isEmpty(historico.nasfPolo)
  const hasProcedimentoAdmin = !isEmpty(procedimentosAdmin)
  const hasCondutas = !isEmpty(historico.condutaDesfecho)
  const hasEncaminhamentos = !isEmpty(historico.encaminhamentos)
  const hasIvcf = !isEmpty(historico.ivcf)

  const hasMchatR = !isEmpty(historico.mchatPontuacao) && !isEmpty(historico.dimensaoMchatRisco)
  const hasMchatRf = !isEmpty(historico.mchatRfPontuacao) && !isEmpty(historico.dimensaoMchatRfRisco)
  const hasMchat = hasMchatR || hasMchatRf

  const hasAvaliacao =
    hasProblemas ||
    historico.nuGestasPrevias ||
    historico.nuPartos ||
    !isEmpty(historico.fatoAppAtendimento?.campoAbertoAvaliacao)

  const hasObjetivo =
    hasExamesAvaliados ||
    hasAnyMedicao ||
    hasIvcf ||
    (historico.dimensaoAleitamento && !hasAllAttrsNull(historico.dimensaoAleitamento)) ||
    hasMedicoesPreNatal ||
    hasMchat ||
    fichasConcatenadas?.uuidZika ||
    !isEmpty(historico.fatoAppAtendimento?.campoAbertoObjetivo)

  const hasPuericultura = (historico.dimensaoAleitamento && !hasAllAttrsNull(historico.dimensaoAleitamento)) || hasMchat

  const cidadaoIdadeEmMeses = calculateAge(
    idadeOrigemAtendimento.dataNascimentoCidadao,
    idadeOrigemAtendimento.dataAtendimento
  ).ageInMonths

  return (
    <HistoricoGapPanel
      idadeOrigemAtendimento={idadeOrigemAtendimento}
      informacoesAdministrativas={mountInfosAdministrativasAtendimentoCompartilhado(historico)}
      isAtendObservacao={historico.stFicouEmObservacao}
      tipoParticipacaoCidadao={historico.dimTipoParticipacaoCidadao?.descricao}
    >
      <HistoricoDwSubjetivoSection historico={historico} ciap2Filtro={ciap2Filtro as Ciap[]} />
      <HistoricoSection section='O'>
        <Flow direction='vertical' gap={0.5}>
          {!hasObjetivo && <Text fontStyle='italic'>{naoRegistradoNesseAtendimento}</Text>}
          {!isEmpty(historico.fatoAppAtendimento?.campoAbertoObjetivo) && (
            <Text>{historico.fatoAppAtendimento?.campoAbertoObjetivo}</Text>
          )}
          {hasAnyMedicao && (
            <HistoricoMedicoesPanel sectionStyle='O' medicoes={medicoes} isAtendimentoObservacao={false} />
          )}
          {hasIvcf && (
            <HistoricoIvcfTableBox
              ivcf={historico.ivcf || undefined}
              riscoVulnerabilidadeText={historico?.ivcf?.dimensaoGrauVulnerabilidadeIvcf?.descricao as string}
            />
          )}
          {hasMedicoesPreNatal && (
            <HistoricoDetailTable title='Medições pré-natal' sectionStyle='O'>
              <Flow direction='vertical' gap={0.5}>
                <Grid>
                  {!isEmpty(historico.stGravidezPlanejada) && (
                    <Cell size={2}>
                      <InfoLabel title='Gravidez planejada'>{historico.stGravidezPlanejada ? 'Sim' : 'Não'}</InfoLabel>
                    </Cell>
                  )}
                  {hasIdadeGestacional && (
                    <Cell size={2}>
                      <InfoLabel title='Idade gestacional'>
                        {'semana'.pluralizeAndConcatValue(historico.nuIdadeGestacional)}
                      </InfoLabel>
                    </Cell>
                  )}
                </Grid>
              </Flow>
            </HistoricoDetailTable>
          )}
          {hasPuericultura && (
            <Flow direction='vertical' gap={0.5}>
              <Heading level={5} style={historicoSubsectionStyles.puericultura}>
                Informações de puericultura
              </Heading>

              {historico.dimensaoAleitamento && !hasAllAttrsNull(historico.dimensaoAleitamento) && (
                <InfoLabel title='Tipo de aleitamento'>{historico.dimensaoAleitamento.descricao}</InfoLabel>
              )}

              {hasMchat && (
                <Flow direction='vertical' gap={0.5}>
                  <Flow direction='horizontal' alignItems='center' justifyContent='space-between'>
                    <Heading level={3} style={historicoSubsectionStyles.puericultura}>
                      M-CHAT-R/F™ (Checklist Modificado para Autismo em Crianças Pequenas)
                    </Heading>
                    <MChatMarca />
                  </Flow>
                  {hasMchatR && (
                    <HistoricoMChatTableBox
                      risco={getMchatRiscoEnumByDimensaoMchatRiscoId(historico.dimensaoMchatRisco?.id)}
                      pontuacao={historico.mchatPontuacao as number}
                      cidadaoIdadeEmMeses={cidadaoIdadeEmMeses}
                    />
                  )}
                  {hasMchatRf && (
                    <HistoricoMChatRfTableBox
                      risco={getMchatRiscoEnumByDimensaoMchatRiscoId(historico.dimensaoMchatRfRisco?.id)}
                      pontuacao={historico.mchatRfPontuacao as number}
                    />
                  )}
                </Flow>
              )}
            </Flow>
          )}
          {hasExamesAvaliados && (
            <HistoricoResultadoExamesTable
              examesResultados={mergeResultadosExamesDw(historico as FatoAtendimentoIndividual)}
            />
          )}
        </Flow>
        {fichasConcatenadas?.uuidZika && (
          <Flow direction='vertical' gap={0.5}>
            <Heading level={3} style={historicoSubsectionStyles.objetivo}>
              Síndrome por Zika/Microcefalia
            </Heading>
            <ResultadosDeExamesZikaContent detalhamento={zikaConcatenada as ResultadoExamesModel} />
          </Flow>
        )}
      </HistoricoSection>
      <HistoricoSection section='A'>
        {hasAvaliacao ? (
          <Flow direction='vertical' gap={0.5}>
            {isFiltroPreenchido(historico.fatoAppAtendimento?.campoAbertoAvaliacao) && (
              <Text>{historico.fatoAppAtendimento?.campoAbertoAvaliacao}</Text>
            )}
            {hasProblemas && (
              <HistoricoAvaliacaoProblemasCondicoesDwTableContent
                problemasCondicoes={
                  historico.problemas as ReadonlyArray<
                    FatoAtendimentoIndividualProblemas | FatoAtendOdontoProblemas | FatoAtendDomProblemaCondicao
                  >
                }
              />
            )}
            <GravidezAvaliacaoSection nuGestasPrevias={historico.nuGestasPrevias} nuPartos={historico.nuPartos} />
          </Flow>
        ) : (
          <Text fontStyle='italic'>{naoRegistradoNesseAtendimento}</Text>
        )}
      </HistoricoSection>
      <HistoricoDwPlanoSection
        historico={historico}
        ciap2Filtro={ciap2Filtro as Ciap[]}
        procedimentos={procedimentosNaoAdmin}
        fichasConcatenadas={fichasConcatenadas}
      />
      <HistoricoSection section='D'>
        <Flow direction='vertical' gap={0.5} justifyItems='stretch'>
          {hasProcedimentoAdmin && (
            <Flow direction='vertical' gap={0.5}>
              <Heading level={5}>Procedimentos Administrativos (SIGTAP)</Heading>
              <HistoricoDetailTable hasLines>
                <Flow direction='vertical' gap={0}>
                  {procedimentosAdmin
                    ?.toSorted((a, b) => a.descricao.localeCompare(b.descricao))
                    .map((procedimento) => (
                      <Text key={procedimento.id}>
                        {procedimento.descricao} - {procedimento.codigo}
                      </Text>
                    ))}
                </Flow>
              </HistoricoDetailTable>
            </Flow>
          )}
          {historico.dimensaoRacionalidadeSaude && !hasAllAttrsNull(historico.dimensaoRacionalidadeSaude) && (
            <InfoLabel title='Racionalidade em saúde'>{historico.dimensaoRacionalidadeSaude?.descricao}</InfoLabel>
          )}
          {hasCondutas && (
            <InfoLabel title='Conduta'>{stringGrouper(...(historico.condutaDesfecho as string[]))}</InfoLabel>
          )}
          {hasEncaminhamentos && (
            <InfoLabel title='Encaminhamento'>{stringGrouper(...(historico.encaminhamentos as string[]))}</InfoLabel>
          )}
        </Flow>
      </HistoricoSection>
      {(hasNasfPolo || historico.dimensaoModalidadeAd) && (
        <HistoricoSection section='I'>
          <Flow direction='vertical' gap={0.5}>
            {historico.dimensaoModalidadeAd && (
              <InfoLabel title='Atenção domiciliar'>{historico.dimensaoModalidadeAd.descricao}</InfoLabel>
            )}
            {hasNasfPolo && (
              <InfoLabel title={(historico.stNasf ? 'NASF' : 'eMulti') + ' / Polo'}>
                {stringGrouper(...(historico.nasfPolo as string[]))}
              </InfoLabel>
            )}
          </Flow>
        </HistoricoSection>
      )}
    </HistoricoGapPanel>
  )
}

export default memo(HistoricoAtendimentoIndividualDwPanel)
